import {
    mat3,
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    vec3,
} from 'gl-matrix';
import {
    AnnotationFrameChange,
    AnnotationPlane,
} from '~/domain/annotation/annotation';
import {
    arePointsOnPlane,
    planeDistanceMm,
    PointMm,
    projectPointOntoPlane,
    toPointMm,
} from '~/domain/geometry/patientGeometry';
import {
    areParallelDirections,
    rotationBetweenNormals,
} from '~/domain/viewport/frameRotation';
import type { Grid, SliceAxis } from '~/engine/data/common/Grid';
import { projectPatientToCanvasPx } from '~/engine/util/geometry/camera';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import { paneImageOf, ShownPaneImage } from '~/engine/view/paneImage';
import { computeViewOrientation } from '~/engine/view/viewOrientation';
import type { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Geometry of the slice a {@link PlaneView} is showing: how thick it is,
 * whether points lie on it, and where they land on the canvas. One module on
 * purpose, because the on-slice decision gates both styling and editability
 * and two drifting copies could show an annotation on a plane it is not in.
 * It holds two such decisions: an annotation, pinned to the plane it was
 * drawn on, is on a slice within half its spacing
 * ({@link arePointsOnDisplayedSlice}); a free 3D point is on the plane within
 * a fixed tolerance and in the one slice that holds it
 * ({@link displayedPlanePlacement}).
 *
 * Beside PlaneView rather than in `domain/` because it reads a live view, or
 * the grid one shows. The view-free half is `domain/geometry/patientGeometry`.
 *
 * A helper given a {@link CanvasFrameGeometry} answers for that frame,
 * published on the view's canvas; without one, for the view's live camera.
 */

/** Fallback half-slice tolerance when no image is loaded (mm). */
const FALLBACK_SLICE_TOLERANCE_MM = 2.5;

/**
 * The image grid axis the acquired slices are stacked along (grid Z). A
 * view whose `imageSliceAxis` is this one lies in the image's own slice
 * planes, whatever the patient orientation of the acquisition.
 */
export const ACQUIRED_SLICE_AXIS: SliceAxis = 2;

/** A point in canvas (screen) pixel coordinates. */
export interface CanvasPoint {
    x: number;
    y: number;
}

/** The normal of the slice shown. */
export function displayedViewDirection(
    view: PlaneView,
    frame?: CanvasFrameGeometry
): ReadonlyVec3 {
    return frame?.viewDirection ?? view.viewDirection;
}

/** The shown plane's offset along its own normal, mm. */
export function displayedPlaneCoordinateMm(
    view: PlaneView,
    frame?: CanvasFrameGeometry
): number {
    return frame
        ? vec3.dot(frame.planeIntersectionPointMm, frame.viewDirection)
        : view.planeCoordinates;
}

/** Signed distance (mm) from `pointMm` to the plane shown, along its normal. */
export function distanceFromDisplayedPlaneMm(
    pointMm: PointMm,
    view: PlaneView,
    frame?: CanvasFrameGeometry
): number {
    return planeDistanceMm(
        pointMm,
        displayedViewDirection(view, frame),
        displayedPlaneCoordinateMm(view, frame)
    );
}

/**
 * Half the through-plane slice spacing (mm) of the image the view shows: the
 * "on this slice" band. Derived in the grid frame, so it survives oblique
 * rotations and tilted grids where dotting a patient-space normal with a
 * grid-frame resolution degenerates.
 */
export function displayedSliceToleranceMm(
    view: PlaneView,
    frame?: CanvasFrameGeometry
): number {
    const pane = paneImageOf(view);
    if (pane.kind === 'none') {
        return FALLBACK_SLICE_TOLERANCE_MM;
    }
    const res = pane.grid.getRes();
    const gridDir = pane.grid.rotateToGrid(displayedViewDirection(view, frame));
    const voxelsPerMm = Math.hypot(
        gridDir[0] / res[0],
        gridDir[1] / res[1],
        gridDir[2] / res[2]
    );
    return Number.isFinite(voxelsPerMm) && voxelsPerMm > 0
        ? 1 / (2 * voxelsPerMm)
        : FALLBACK_SLICE_TOLERANCE_MM;
}

/**
 * Whether `view` shows an acquired slice of its Environment's primary
 * image: its plane lies in one of the image's own slice planes, and it
 * draws the primary image, alone or blended. A view that draws the fused
 * secondary image alone shows that image resampled through the
 * registration, not a slice of the primary, so a slice-scoped action on
 * the primary would change a slice no view shows.
 */
export function viewShowsAcquiredPrimarySlice(view: PlaneView): boolean {
    return (
        view.imageSliceAxis === ACQUIRED_SLICE_AXIS &&
        paneImageOf(view).kind === 'primary'
    );
}

/**
 * Whether `plane` belongs to `view`: the view shows the image of the
 * plane's series, primary or fused secondary, in the same orientation. The
 * orientation counts because an annotation may only be edited in the view
 * it was drawn in. Steering the crosshair to one is a looser question, and
 * is answered by the series alone.
 */
export function viewShowsPlane(
    view: PlaneView,
    plane: AnnotationPlane
): boolean {
    const pane = paneImageOf(view);
    return (
        pane.kind !== 'none' &&
        pane.seriesUid === plane.seriesInstanceUid &&
        view.label === plane.orientation
    );
}

/** The change from the pane's frame into the frame of the image it shows. */
export function paneToImageFrame(pane: ShownPaneImage): AnnotationFrameChange {
    return { point: pane.toImageFrame, plane: pane.planeToImageFrame };
}

/** The change from the frame of the image the pane shows onto the pane. */
export function imageToPaneFrame(pane: ShownPaneImage): AnnotationFrameChange {
    return { point: pane.fromImageFrame, plane: pane.planeFromImageFrame };
}

/** The normal of the standard `plane.orientation` view on `grid`. */
function standardNormal(plane: AnnotationPlane, grid: Grid): vec3 {
    const base = computeViewOrientation(grid, plane.orientation);
    // Column-major: the third column is the normal.
    return vec3.fromValues(base[6], base[7], base[8]);
}

/**
 * Whether views turned to `frameRotation` look along `plane`, either way up
 * and whatever their roll about it.
 */
export function frameShowsPlane(
    frameRotation: ReadonlyMat3,
    plane: AnnotationPlane,
    grid: Grid
): boolean {
    const shownNormal = vec3.transformMat3(
        vec3.create(),
        standardNormal(plane, grid),
        frameRotation
    );
    return areParallelDirections(shownNormal, plane.viewDirection);
}

/**
 * The frame rotation under which the `plane.orientation` view looks along
 * `plane.viewDirection` again, from the side it was placed on: the smallest
 * turn from its standard normal on `grid`, identity when it already does.
 */
export function frameRotationShowingPlane(
    plane: AnnotationPlane,
    grid: Grid
): mat3 {
    return rotationBetweenNormals(
        standardNormal(plane, grid),
        plane.viewDirection
    );
}

/**
 * Whether `view` looks along `plane`, either way up, and EVERY point lies on
 * the shown slice within `toleranceMm`. Points alone do not decide: a line
 * along the intersection of two planes lies on both, and an edit there would
 * leave the plane its record names. The slice test is true point-to-plane
 * distance, never the stored `planeCoordinateMm` against the view's, which
 * differ in sign from the other side of the plane. `points` and `plane` are
 * in the pane's frame ({@link imageToPaneFrame}).
 */
export function arePointsOnDisplayedSlice(
    points: readonly PointMm[],
    view: PlaneView,
    plane: AnnotationPlane,
    toleranceMm: number,
    frame?: CanvasFrameGeometry
): boolean {
    const direction = displayedViewDirection(view, frame);
    if (
        !viewShowsPlane(view, plane) ||
        !areParallelDirections(direction, plane.viewDirection)
    ) {
        return false;
    }
    return arePointsOnPlane(
        points,
        direction,
        displayedPlaneCoordinateMm(view, frame),
        toleranceMm
    );
}

/**
 * Whether `pointMm` lies in the slice `view` shows. With an image loaded,
 * every point lies in exactly one slice of the stack the view scrolls
 * through: on an axis-aligned grid seen along one of its axes, the image
 * slice holding the plane, floored as {@link Grid.snapPositionToSliceCenter}
 * floors, so a point on a slice boundary belongs to the slice above it;
 * elsewhere, the half-open band of one scroll step
 * ({@link Grid.voxelSpacingAlong}) centred on the plane. With no image, the
 * closed band of {@link displayedSliceToleranceMm}.
 */
export function isPointOnDisplayedSlice(
    pointMm: PointMm,
    view: PlaneView
): boolean {
    const grid = view.environment.image?.grid;
    const distanceMm = planeDistanceMm(
        pointMm,
        view.viewDirection,
        view.planeCoordinates
    );
    if (!grid) {
        return Math.abs(distanceMm) <= displayedSliceToleranceMm(view);
    }
    const axis = view.imageSliceAxis;
    if (axis !== undefined && grid.isAxisAligned()) {
        const planePointMm =
            view.environment.planeIntersectionPoint.coordinates;
        return (
            Math.floor(grid.toVoxelSpace(pointMm)[axis]) ===
            Math.floor(grid.toVoxelSpace(planePointMm)[axis])
        );
    }
    const halfStepMm = grid.voxelSpacingAlong(view.viewDirection) / 2;
    return distanceMm >= -halfStepMm && distanceMm < halfStepMm;
}

/**
 * Largest distance (mm) from the displayed plane at which a point is drawn as
 * on it, so a position read off the image is within this of the stored one.
 */
const ON_PLANE_TOLERANCE_MM = 0.5;

/** Where a point lies relative to the plane and slice a view shows. */
export type DisplayedPlanePlacement = 'onPlane' | 'inSlice' | 'offSlice';

/**
 * Where `pointMm` lies relative to what `view` shows: `offSlice` outside the
 * displayed slice ({@link isPointOnDisplayedSlice}); within it, `onPlane`
 * strictly within {@link ON_PLANE_TOLERANCE_MM} of the plane, or of half the
 * slice spacing where that is smaller, so a point is on plane at one scroll
 * position at most, even on a slice boundary; otherwise `inSlice`. On a view
 * that draws the fused secondary image alone the two checks read different
 * images: the slice test reads the primary image's slices, where the point's
 * structure set is edited, and the on-plane band reads the slices of the
 * image the view shows ({@link displayedSliceToleranceMm}).
 */
export function displayedPlanePlacement(
    pointMm: PointMm,
    view: PlaneView
): DisplayedPlanePlacement {
    if (!isPointOnDisplayedSlice(pointMm, view)) {
        return 'offSlice';
    }
    const distanceMm = planeDistanceMm(
        pointMm,
        view.viewDirection,
        view.planeCoordinates
    );
    const toleranceMm = Math.min(
        ON_PLANE_TOLERANCE_MM,
        displayedSliceToleranceMm(view)
    );
    return Math.abs(distanceMm) < toleranceMm ? 'onPlane' : 'inSlice';
}

/** Midpoint of two canvas points — where an annotation's label sits. */
export function canvasMidpoint(a: CanvasPoint, b: CanvasPoint): CanvasPoint {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Where a patient-space mm point lands on the canvas shown, px. */
export function displayedCanvasPx(
    pointMm: ReadonlyVec3,
    view: Pick<PlaneView, 'canvasCoordsFromPatientCoords'>,
    frame?: CanvasFrameGeometry
): ReadonlyVec2 {
    return frame
        ? projectPatientToCanvasPx(
              pointMm,
              frame.projectionViewMatrix,
              frame.sizePx
          )
        : view.canvasCoordsFromPatientCoords(pointMm);
}

/** Project a patient-space mm point to canvas (screen) coordinates. */
export function patientToCanvasPoint(
    pointMm: PointMm,
    view: PlaneView,
    frame?: CanvasFrameGeometry
): CanvasPoint {
    const [x, y] = displayedCanvasPx(pointMm, view, frame);
    return { x, y };
}

/** A window (client) position as a patient-frame mm point. */
export function clientToPatientPoint(
    clientXY: ReadonlyVec2,
    view: PlaneView
): PointMm {
    return toPointMm(view.clientToPatientCoordinates(clientXY));
}

/**
 * A point's slice position in `view`: its projection onto the view normal, mm.
 * Pass to {@link pinToViewPlane} to pin to that point's slice, not the shown one.
 */
export function viewPlaneCoordinateMm(
    pointMm: PointMm,
    view: PlaneView
): number {
    return planeDistanceMm(pointMm, view.viewDirection, 0);
}

/** `pointMm` moved along the view normal onto `planeCoordinateMm`. */
export function pinToViewPlane(
    pointMm: PointMm,
    view: PlaneView,
    planeCoordinateMm: number
): PointMm {
    return projectPointOntoPlane(
        pointMm,
        view.viewDirection,
        planeCoordinateMm
    );
}

/** Whether the image the view shows has a voxel at this pane point. */
export function isPointInsideDisplayedImage(
    pointMm: PointMm,
    view: PlaneView
): boolean {
    const pane = paneImageOf(view);
    return pane.kind !== 'none' && pane.grid.includesPoint(pointMm);
}
